import React, { Suspense } from "react";
import { Alert, Button, Col, Modal, Row, Spinner } from "react-bootstrap";
import { BoxArrowLeft } from "react-bootstrap-icons";
import { Outlet } from "react-router-dom";
import { LayoutProps, LayoutState } from "../../types/appTypes";
import { doPost } from "../../utils/utils";

// PERF-H9: react-select is only needed for the corporate org picker rendered conditionally below
// (`corporateUserOrgs.length && showOrgSelect`), but Layout is the parent element of EVERY route
// (see App.tsx's <Route path="/" element={<Layout .../>}>) -- it renders on /home and /login too,
// for visitors who will never see the selector at all. A plain `import Select from "react-select"`
// here therefore put react-select in the ENTRY chunk for every visitor regardless of that
// condition; the condition only ever gated whether the JSX got RENDERED, not whether the module got
// LOADED. OrgSelect.tsx wraps the actual react-select usage behind its own module so this
// React.lazy() call is a genuine code-split point. This is only half of PERF-H9's react-select
// split: LinkForm.tsx's own two react-select imports are handled by App.tsx now loading LinkForm
// itself through React.lazy() -- see the comment there. Do not change this back to a static import
// of "./OrgSelect" or "react-select" (see entryChunkGuard.test.ts, extended for PERF-H9).
const OrgSelect = React.lazy(() => import("./OrgSelect"));

// Fixed-height placeholder shown while OrgSelect's chunk is still downloading, on first render of
// the corporate org selector. 38px matches react-select's own default single-line control height
// (its default theme's `spacing.controlHeight`), so swapping this fallback for the real <Select>
// does not shift anything else in the header (CLS) -- a corporate user sees a stable-sized
// placeholder here, never a layout jump.
const orgSelectFallback = (
	<div className="d-flex justify-content-center align-items-center" style={{ height: 38 }}>
		<Spinner animation="border" variant="primary" size="sm" role="status">
			<span className="visually-hidden">Loading organization selector...</span>
		</Spinner>
	</div>
);

export default class Layout extends React.Component<LayoutProps, LayoutState> {
	constructor(props: any) {
		super(props);
		this.state = {
			showAlphaWarning: false, //For now its stable, but the component is useful for other notifications.
			showLogOutModal: false,
			integration: process.env.REACT_APP_INTEGRATION_SERVICE,
		};
	}

	hideErrorModal = () => {
		this.props.setGeneralError(false);
	};

	hideWarningModal = () => {
		this.setState({ showAlphaWarning: false });
	};

	handleLogOut = async () => {
		let url = "";
		try {
			if (this.state.integration === "GitLab") {
				url = `${this.props.integrationUrl}/api/auth/logout`;
				await doPost(url, {});
			} else {
				url = `${this.props.integrationUrl}/api/logout`;
				await doPost(url, {}, this.props.userToken);
			}
		} catch (e: any) {
			// Surface the failure, but never let it block the local logout below - the user asked to
			// log out, and leaving them stuck in a logged-in UI because the server call failed is
			// strictly worse than a stale server-side session.
			this.props.setGeneralError(e?.message || "Could not reach the server to log out, but you have been logged out locally.");
		} finally {
			// Always run, whether the server call succeeded or failed: the local session must end and
			// the confirmation modal must close either way.
			this.props.logOut();
			this.setState({ showLogOutModal: false });
		}
	};

	getSelectOptsFormStringArr = (arr: string[]) => {
		return arr.map(item => {
			return {
				value: item,
				label: item,
			};
		});
	};

	hideLogOutModal = () => {
		this.setState({ showLogOutModal: false });
	};

	render(): React.ReactNode {
		const user_orgs_select = this.getSelectOptsFormStringArr(this.props.corporateUserOrgs);
		const title = `Businessmap to ${this.state.integration ? this.state.integration : ""}
		${process.env.REACT_APP_INTEGRATION_VERSION ? " (" + process.env.REACT_APP_INTEGRATION_VERSION + ")" : ""} 
		integration`;
		return (
			<>
				<Row className="mt-3 mb-3">
					<Col></Col>
					<Col xs={9}>
						<div className="text-center">
							<h3>{title}</h3>
						</div>
					</Col>
					<Col className="d-flex justify-content-end">
						<Button
							className={
								!(this.props.userToken || this.props.corporateUserOrgs.length)
									? "d-none"
									: "d-flex align-items-center py-2"
							}
							variant="danger"
							onClick={() => this.setState({ showLogOutModal: true })}
						>
							<BoxArrowLeft />
						</Button>
					</Col>
				</Row>
				{/* TODO:  Handle the multiple orgs case*/}
				{!!this.props.corporateUserOrgs.length && this.props.showOrgSelect && (
					<Row className="mt-3 mb-3">
						<Col></Col>
						<Col>
							<div className="text-center">
								<Suspense fallback={orgSelectFallback}>
									<OrgSelect
										options={user_orgs_select}
										value={user_orgs_select.filter(item => item.value === this.props.currentUserOrg)}
										onChange={val => val && this.props.setUserOrg(val.value)}
									/>
								</Suspense>
							</div>
						</Col>
						<Col></Col>
					</Row>
				)}

				<Modal show={!!this.state.showLogOutModal} onHide={this.hideLogOutModal}>
					<Modal.Header>Log out</Modal.Header>
					<Modal.Body>Are you sure you want to log out?</Modal.Body>
					<Modal.Footer>
						<Button variant="secondary" onClick={() => this.hideLogOutModal()}>
							Close
						</Button>
						<Button variant="danger" onClick={this.handleLogOut}>
							Log out
						</Button>
					</Modal.Footer>
				</Modal>
				<Alert
					show={this.state.showAlphaWarning}
					variant="warning"
					className="m-3"
					onClose={this.hideWarningModal}
					dismissible
				>
					<Alert.Heading> WARNING </Alert.Heading>
					<p>
						{" "}
						This is an alpha version of the integration between{" "}
						{process.env.INTEGRATION_SERVICE ? process.env.INTEGRATION_SERVICE : ""} and Businessmap if you detect any bugs
						please contact us at support@businessmap.io{" "}
					</p>
				</Alert>
				<Alert className="m-3" show={!!this.props.generalError} variant="danger" onClose={this.hideErrorModal} dismissible>
					<Alert.Heading> ERROR </Alert.Heading>
					{this.props.generalError &&
						String(this.props.generalError)
							.split(/\r?\n/)
							.map((line, i) => (
								<p className="mb-0" key={i}>
									{line}
								</p>
							))}
					{/* <p>{this.props.generalError}</p> */}
				</Alert>
				<Outlet />
			</>
		);
	}
}
